<template>
  <header class="page-header-container">
    <div class="header-content">
      <!-- Logo Section -->
      <div class="header-section logo-section">
        <div class="logo-wrapper" @click="router.push('/')">
          <img src="/logo/artfield-logo-black.png" alt="Artfield Logo" class="logo-image" />
        </div>
      </div>

      <!-- Navigation Links Section -->
      <div class="header-section nav-section">
        <nav class="nav-links">
          <a @click.prevent="scrollToSection('about')" 
             href="/#about" 
             class="nav-link-item" 
             :class="{ 'active': activeSection === 'about' }">
            ABOUT
          </a>
          <a @click.prevent="scrollToSection('talents')" 
             href="/#talents" 
             class="nav-link-item" 
             :class="{ 'active': activeSection === 'talents' }">
            TALENTS
          </a>
          <a @click.prevent="scrollToSection('reads')" 
             href="/#reads" 
             class="nav-link-item" 
             :class="{ 'active': activeSection === 'reads' }">
            READS
          </a>
          <a @click.prevent="scrollToSection('download')" 
             href="/#download" 
             class="nav-link-item" 
             :class="{ 'active': activeSection === 'download' }">
            DOWNLOAD APP
          </a>
          <a @click.prevent="scrollToSection('contact')" 
             href="/#contact" 
             class="nav-link-item" 
             :class="{ 'active': activeSection === 'contact' }">
            CONTACT
          </a>
        </nav>
      </div>

      <!-- Auth Buttons Section -->
      <div class="header-section auth-section">
        <div class="auth-buttons">
          <a v-if="!user" href="/welcome-to-artfield" class="auth-button">REGISTER</a>
          <a v-if="!user" href="/login" class="auth-button">LOG IN</a>
          <a v-else href="/field/feed" class="auth-button">THE FIELD</a>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';

const router = useRouter();

const {user} = useUserStore();
defineProps({
  activeSection: {
    type: String,
    default: 'home'
  }
})
const scrollToSection = (sectionId: string) => {
  const element = document.getElementById(sectionId)
  if (element) {
    const offset = 20
    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
    window.scrollTo({
      top: elementPosition - offset,
      behavior: 'smooth'
    })
  }
}
</script>

<style scoped>
/* Root custom property for header height */
:root {
  --header-height: 50px;
}

.page-header-container {
  padding: 10px 40px;
  background-color: transparent;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: var(--header-height);
  display: flex;
  align-items: center;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1360px;
  width: 100%;
  margin: 0 auto;
}

.header-section {
  background-color: rgba(235, 235, 235, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 999px;
  display: flex;
  align-items: center;
  opacity: 1;
  padding: 6px;
  border: 1px solid rgba(235, 235, 235, 0.3);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

/* Logo specific styles */
.logo-section {
  padding-right: 0;
  background-color: rgba(235, 235, 235, 0.7);
}

.logo-wrapper {
  border-radius: 100px;
  padding: 4px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.logo-image {
  height: 13px;
  width: auto;
  display: block;
}

/* Navigation styles */
.nav-section {
  gap: 6px;
}

.nav-links {
  display: flex;
  gap: 6px;
}

.nav-link-item {
  border: 1px solid rgba(0, 0, 0, 0.5);
  border-radius: 100px;
  padding: 4px 16px;
  text-decoration: none;
  font-weight: 400;
  font-size: 10px;
  line-height: 1;
  vertical-align: middle;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.5);
  text-align: center;
  transition: all 0.2s ease;
  cursor: pointer;
}

.nav-link-item:hover, .nav-link-item.active {
  background-color: #fff;
  border-color: #fff;
  color: rgba(0, 0, 0, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.nav-link-item.active {
  background-color: #fff;
  border-color: #fff;
  color: rgba(0, 0, 0, 1);
  font-weight: 500;
}

/* Auth styles */
.auth-section {
  gap: 6px;
}

.auth-buttons {
  display: flex;
  gap: 6px;
}

.auth-button {
  border: 1px solid rgba(0, 0, 0, 0.5);
  border-radius: 100px;
  padding: 4px 16px;
  text-decoration: none;
  font-weight: 400;
  font-size: 10px;
  align-self: center;
  line-height: 1;
  vertical-align: middle;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.5);
  text-align: center;
  transition: all 0.2s ease;
}

.auth-button:hover {
  background-color: #fff;
  border-color: #fff;
  color: rgba(0, 0, 0, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* Responsive styles */
@media (max-width: 900px) {
  .header-content {
    flex-direction: column;
    gap: 10px;
  }
  
  .header-section {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 600px) {
  .nav-links, .auth-buttons {
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px;
  }
  
  .nav-link-item, .auth-button {
    padding: 3px 10px;
    font-size: 9px;
  }
  
  .page-header-container {
    padding: 10px 20px;
  }
}
</style> 